import { Await, useLoaderData, useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuthUserContext } from '../../../providers/AuthUserProvider';
import { TeacherInfo, TeacherProfileVariant } from '../../../types/TeacherInfo';
import { EstudiApiClient } from '../../../services/interfaces/EstudiApiClient/EstudiApiClient';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import {
  Alert,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Link,
  Skeleton
} from '@mui/material';
import ProfileImage from '../../ProfileImage';
import Rate from '../../Rate';
import { UpdateTeacherInfo } from '../../../types/UpdateTeacherInfo';
import EditCoursesSelector from './EditCoursesSelector';
import { Course } from '../../../types/Course';
import { Institution } from '../../../types/Institution';
import { verticalCopy } from '../../../utils/constants/verticalCopy';
import { InstitutionTypeFilter } from '../../../services/adapters/queries/institutionsQueryFunction';
import {
  containsPhoneNumber,
  PHONE_NUMBER_IN_DESCRIPTION_MESSAGE
} from '../../../utils/functions/containsPhoneNumber';
import './EditTeacherProfile.css';

interface EditTeacherProfileLoaderResponse {
  teacher: TeacherInfo;
  institutions: Promise<Institution[]>;
  coursesByInstitution: Promise<Record<string, Course[]>>;
}

const PARTICIPATION_TYPES: Exclude<InstitutionTypeFilter, 'all'>[] = [
  'university',
  'languages',
  'schools'
];

const emptyProfileVariant = (): TeacherProfileVariant => ({
  profile_description: '',
  full_description: '',
  price_per_hour: 0
});

const PROFILE_IMAGE_HELP_LINK = `https://wa.me/5491161297377?text=${encodeURIComponent(
  'Hola! Quiero cambiar la foto de mi perfil de profe.'
)}`;

type ProfileDescriptionField = 'profile_description' | 'full_description';

type PhoneNumberErrors = Record<string, Partial<Record<ProfileDescriptionField, string>>>;

const findPhoneNumberErrors = (
  profiles: Record<string, TeacherProfileVariant>
): PhoneNumberErrors =>
  Object.entries(profiles).reduce<PhoneNumberErrors>((errors, [type, variant]) => {
    const fieldsWithPhoneNumber: ProfileDescriptionField[] = (
      ['profile_description', 'full_description'] as ProfileDescriptionField[]
    ).filter((field) => containsPhoneNumber(variant[field]));

    if (fieldsWithPhoneNumber.length === 0) {
      return errors;
    }

    return {
      ...errors,
      [type]: Object.fromEntries(
        fieldsWithPhoneNumber.map((field) => [field, PHONE_NUMBER_IN_DESCRIPTION_MESSAGE])
      )
    };
  }, {});

const EditTeacherProfile: React.FC<{ estudiClient: EstudiApiClient }> = ({ estudiClient }) => {
  const { user } = useAuthUserContext();
  const { teacher, institutions, coursesByInstitution } =
    useLoaderData() as EditTeacherProfileLoaderResponse;
  const [formData, setFormData] = useState<UpdateTeacherInfo>({});
  const [profiles, setProfiles] = useState<Record<string, TeacherProfileVariant>>({});
  const [profilesError, setProfilesError] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    setFormData({
      available_days_of_week: teacher.available_days_of_week,
      dictated_courses: teacher.dictated_courses,
      configuration: teacher.configuration
    });
    setProfiles(teacher.teacher_profiles ?? {});
  }, [teacher]);

  const handleToggleParticipation = (type: string, participates: boolean) => {
    setProfiles((prev) => {
      if (participates) {
        return { ...prev, [type]: prev[type] ?? emptyProfileVariant() };
      }

      if (Object.keys(prev).length <= 1) {
        setProfilesError('Debés participar en al menos una vertical.');

        return prev;
      }

      setProfilesError(undefined);
      const { [type]: ignored, ...rest } = prev;

      return rest;
    });
  };

  const handleProfileFieldChange = (
    type: string,
    field: keyof TeacherProfileVariant,
    value: string
  ) => {
    setProfiles((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] ?? emptyProfileVariant()),
        [field]: field === 'price_per_hour' ? Number(value) : value
      }
    }));
  };

  const handleInputConfigurationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    isNumeric = false
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...teacher.configuration,
        ...prev.configuration,
        [name]: isNumeric ? parseInt(value) : value
      }
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...teacher.configuration,
        ...prev.configuration,
        [name]: checked
      }
    }));
  };

  const handleCoursesChange = (updatedCourses: Course[]) => {
    setFormData((prev) => ({
      ...prev,
      dictated_courses: updatedCourses
    }));
  };

  const phoneNumberErrors = useMemo(() => findPhoneNumberErrors(profiles), [profiles]);
  const hasPhoneNumberErrors = Object.keys(phoneNumberErrors).length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(profiles).length === 0) {
      setProfilesError('Debés participar en al menos una vertical.');

      return;
    }
    if (hasPhoneNumberErrors) {
      setProfilesError(PHONE_NUMBER_IN_DESCRIPTION_MESSAGE);

      return;
    }
    try {
      if (user?.id) {
        await estudiClient.updateTeacherProfile({
          teacherId: user.id,
          profileData: { ...formData, teacher_profiles: profiles }
        });

        navigate(`/teachers/${user.id}/profile`);
      }
    } catch (error: unknown) {
      console.error('Error updating teacher profile:', error);
      alert('Hubo un error actualizando el perfil.');
    }
  };

  const participatingTypes = PARTICIPATION_TYPES.filter((type) => type in profiles);

  return (
    <div className="editTeacherProfile">
      <h1>Edita tu Perfil</h1>
      <form onSubmit={handleSubmit} className="editForm">
        {/* Personal information */}
        <div className="teacherPersonalInfo">
          <Card>
            <CardContent sx={{ padding: { xs: '12px', sm: '20px' } }}>
              <div className="teacherCardHeader">
                <ProfileImage image_url={teacher.image_url} size="large" />
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    fontSize: {
                      sm: '2rem',
                      xs: '1.5rem'
                    }
                  }}
                  className="teacherCardName">
                  {teacher.first_name} {teacher.last_name}
                  {teacher.rate && teacher.rate.average && teacher.rate.quantity && (
                    <Rate rate={teacher.rate} />
                  )}
                </Typography>
              </div>
              <Alert severity="info" sx={{ marginTop: '16px' }}>
                Por ahora la foto de perfil no se puede cambiar desde acá. Si querés actualizarla,
                escribinos por{' '}
                <Link
                  href={PROFILE_IMAGE_HELP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover">
                  WhatsApp al +5491161297377
                </Link>{' '}
                y el equipo de estudi la cambia por vos.
              </Alert>
              <Divider sx={{ margin: '20px 0' }}>Verticales en las que participo</Divider>
              {profilesError && <Alert severity="error">{profilesError}</Alert>}
              {PARTICIPATION_TYPES.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={type in profiles}
                      onChange={(e) => handleToggleParticipation(type, e.target.checked)}
                    />
                  }
                  label={verticalCopy[type].contextLabel}
                />
              ))}
            </CardContent>
          </Card>
        </div>

        {participatingTypes.map((type) => (
          <div className="teacherPersonalInfo" key={type}>
            <Card>
              <CardContent sx={{ padding: { xs: '12px', sm: '20px' } }}>
                <Divider sx={{ margin: '20px 0' }}>
                  Perfil para {verticalCopy[type].contextLabel}
                </Divider>
                <TextField
                  label="Descripción Resumen"
                  value={profiles[type]?.profile_description || ''}
                  onChange={(e) =>
                    handleProfileFieldChange(type, 'profile_description', e.target.value)
                  }
                  error={Boolean(phoneNumberErrors[type]?.profile_description)}
                  helperText={phoneNumberErrors[type]?.profile_description}
                  fullWidth
                  margin="normal"
                  required
                />
                <TextField
                  label="Descripción Completa"
                  value={profiles[type]?.full_description || ''}
                  onChange={(e) =>
                    handleProfileFieldChange(type, 'full_description', e.target.value)
                  }
                  error={Boolean(phoneNumberErrors[type]?.full_description)}
                  helperText={phoneNumberErrors[type]?.full_description}
                  multiline
                  rows={4}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Precio por hora"
                  type="number"
                  value={profiles[type]?.price_per_hour || ''}
                  onChange={(e) => handleProfileFieldChange(type, 'price_per_hour', e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
              </CardContent>
            </Card>
          </div>
        ))}

        {/* Classes configuration */}
        <div className="teacherConfiguration">
          <Card>
            <CardContent sx={{ padding: { xs: '12px', sm: '20px' } }}>
              <Divider sx={{ margin: '20px 0' }}>Configuración de Clases</Divider>
              <FormControlLabel
                control={
                  <Checkbox
                    name="virtual_classes"
                    checked={formData.configuration?.virtual_classes || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Cómo das las clases virtuales?"
              />
              <TextField
                label="Por ejemplo, podes contar que dias"
                name="virtual_classes_message"
                value={formData.configuration?.virtual_classes_message || ''}
                onChange={handleInputConfigurationChange}
                disabled={!formData.configuration?.virtual_classes}
                fullWidth
                margin="normal"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="in_person_classes"
                    checked={formData.configuration?.in_person_classes || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Cómo das las clases presenciales?"
              />
              <TextField
                label="Por ejemplo, podes contar donde y que dias"
                name="in_person_classes_message"
                value={formData.configuration?.in_person_classes_message || ''}
                onChange={handleInputConfigurationChange}
                disabled={!formData.configuration?.in_person_classes}
                fullWidth
                margin="normal"
              />

              <TextField
                label="Semanas en adelante disponibles en el calendario"
                name="weeks_in_advance_for_reserve"
                type="number"
                value={formData.configuration?.weeks_in_advance_for_reserve || ''}
                onChange={(e) => handleInputConfigurationChange(e, true)}
                fullWidth
                margin="normal"
              />
            </CardContent>
          </Card>
        </div>

        {/* Dictated courses */}
        <div className="teacherDictatedCourses">
          <Card>
            <CardContent sx={{ padding: { xs: '12px', sm: '20px' } }}>
              <Divider sx={{ margin: '20px 0' }}>Materias Dictadas</Divider>
              <React.Suspense
                fallback={
                  <Skeleton
                    variant="rounded"
                    animation="pulse"
                    width={'100%'}
                    height={800}
                    sx={{ marginTop: '2%' }}
                  />
                }>
                <Await
                  resolve={useMemo(() => Promise.all([institutions, coursesByInstitution]), [])}
                  errorElement={<p>Ocurrió un error cargando los datos de materias.</p>}>
                  {([loadedInstitutions, loadedCoursesByInstitution]: [
                    Institution[],
                    Record<string, Course[]>
                  ]) => (
                    <div className="editCoursesForm">
                      <FormHelperText>
                        <a
                          href="https://drive.google.com/drive/folders/1txRK2dXrJl0RxSskR8P3twOry1TUoiQk"
                          target="_blank"
                          rel="noopener noreferrer">
                          Acá
                        </a>{' '}
                        podrás ver material que hemos recopilado de las distintas materias para que
                        puedas evaluar si podes dar dicha materia. <br /> Marca las materias que
                        dictas, organizadas por institución. También puedes agregar notas
                        adicionales para cada materia.
                      </FormHelperText>
                      <EditCoursesSelector
                        institutions={loadedInstitutions}
                        institutionTypes={participatingTypes}
                        coursesByInstitution={new Map(Object.entries(loadedCoursesByInstitution))}
                        dictatedCourses={teacher.dictated_courses}
                        onChange={handleCoursesChange}
                      />
                    </div>
                  )}
                </Await>
              </React.Suspense>
            </CardContent>
          </Card>
        </div>

        {/* Save button */}
        {hasPhoneNumberErrors && (
          <Alert severity="error" sx={{ marginTop: '20px' }}>
            {PHONE_NUMBER_IN_DESCRIPTION_MESSAGE}
          </Alert>
        )}
        <Button type="submit" variant="contained" color="primary" sx={{ marginTop: '20px' }}>
          Guardar Cambios
        </Button>
      </form>
    </div>
  );
};

export default EditTeacherProfile;
